import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, CheckCircle, ArrowRight, Mail, Calendar, 
  Shield, Zap, Phone, HelpCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const companyId = searchParams.get('company');
  const isTrial = searchParams.get('trial') === 'true';
  const pendingReg = JSON.parse(localStorage.getItem('pending_registration') || '{}');
  const [trialDays, setTrialDays] = useState(7);
  
  useEffect(() => {
    // Fetch trial days from API
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/onboarding/config`);
        if (res.ok) {
          const data = await res.json();
          setTrialDays(data.trial_days || 7);
        }
      } catch (e) {
        console.log('Could not fetch config');
      }
    };
    fetchConfig();
  }, []);
  
  // Build subscribe URL with locations and staff
  const subscribeUrl = `/onboarding/payment?company=${companyId || pendingReg.company_id}&plan=${pendingReg.selected_plan || 'starter_monthly'}&locations=${pendingReg.num_spa_locations || 1}&staff=${pendingReg.num_staff_to_manage || 1}`;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="max-w-xl w-full">
        <Card className="bg-slate-800/50 border-slate-700 text-center">
          <CardHeader className="pt-10">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6 animate-bounce">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-3xl text-white mb-2">
              Welcome to WellNest Technologies!
            </CardTitle>
            <CardDescription className="text-slate-400 text-lg">
              Your account has been created successfully
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6 pb-10">
            {isTrial && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-4 py-2 text-sm">
                <Zap className="w-4 h-4 mr-2" />
                Your Free Trial Has Started!
              </Badge>
            )}
            
            <div className="bg-slate-700/30 rounded-xl p-6 space-y-4 text-left">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-teal-400" />
                Check Your Email
              </h3>
              <p className="text-slate-400">
                We've sent your login credentials to{' '}
                <span className="text-white font-medium">{pendingReg.admin_email || 'your email'}</span>.
                Check your inbox (and spam folder) for the welcome email.
              </p>
            </div>
            
            <div className="bg-slate-700/30 rounded-xl p-6 space-y-4 text-left">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                What's Next?
              </h3>
              <ul className="space-y-3 text-slate-400">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Log in with your credentials and explore the dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Add your spa properties and therapists</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Set up your services and start accepting bookings</span>
                </li>
                {isTrial && (
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Subscribe before your trial ends to continue uninterrupted</span>
                  </li>
                )}
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                onClick={() => navigate('/login')}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 h-12 text-lg"
              >
                Go to Login <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              {isTrial && (
                <Button 
                  variant="outline"
                  onClick={() => navigate(subscribeUrl)}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700 h-12"
                >
                  Subscribe Now
                </Button>
              )}
            </div>
            
            <div className="pt-6 border-t border-slate-700">
              <p className="text-slate-500 text-sm flex items-center justify-center gap-2">
                <HelpCircle className="w-4 h-4" />
                Need help? Contact support@wellnesttechnologies.com
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
